import {
  type CreateTestParameterInput,
  PARAMETER_FORMULAS,
  PARAMETER_FORMULA_KEYS,
  type ParameterFormula,
  type TestParameter,
} from '#shared/types';

export type RangeRow = CreateTestParameterInput['values'][number];
export type ResultRow = CreateTestParameterInput['results'][number];

/**
 * A reference range with no cohort set — what a parameter starts with.
 *
 * Module scope because `useRepeater` closes over it: a fresh object literal
 * each render would make its `add` callback unstable.
 */
export const EMPTY_RANGE: RangeRow = {
  normal_range: '',
  unit: '',
  age: '',
  age_group: '',
  gender: null,
  other: '',
};

export const EMPTY_RESULT: ResultRow = { value: '' };

export interface ParameterIdentity {
  name: string;
  code: string;
  formula: ParameterFormula | null;
}

export function initialIdentity(record?: TestParameter): ParameterIdentity {
  return { name: record?.name ?? '', code: record?.code ?? '', formula: record?.formula ?? null };
}

/** Every formula as a select option, reading "MCV — HCT / RBC × 10". */
export const FORMULA_OPTIONS = PARAMETER_FORMULA_KEYS.map((key) => ({
  value: key,
  label: `${PARAMETER_FORMULAS[key].label} — ${PARAMETER_FORMULAS[key].expression}`,
}));

/** Which codes a formula reads, for the help text under the select. */
export function formulaHelp(formula: ParameterFormula | null): string {
  if (formula === null) return 'Leave empty for a result the technician types.';
  const inputs = PARAMETER_FORMULAS[formula].inputs.join(', ');
  return `Calculated on entry from ${inputs} in the same test; never typed.`;
}

/** The saved ranges, with stored nulls as empty text; one blank range for a new parameter. */
export function initialRanges(record?: TestParameter): RangeRow[] {
  if (!record) return [EMPTY_RANGE];
  return record.values.map((range) => ({
    normal_range: range.normal_range,
    unit: range.unit,
    age: range.age ?? '',
    age_group: range.age_group ?? '',
    gender: range.gender,
    other: range.other ?? '',
  }));
}

/** The saved preset results; none for a new parameter. */
export function initialResults(record?: TestParameter): ResultRow[] {
  return record?.results.map((result) => ({ value: result.value })) ?? [];
}
